// admin-ticket-analytics-dashboard S1 — Ticket Analytics API client
//
// Provides typed wrappers for both analytics endpoints:
//   - getLocationAnalytics(locationId) → GET /admin/analytics/locations/:id (org_admin | supervisor)
//   - getOrgAnalytics()                → GET /admin/analytics/org           (org_admin only)
//
// Mirrors the shape of audit-log.ts (buildQueryString + runtime-validated response).

import type {
  AnalyticsRangeQuery,
  LocationAnalyticsResponse,
  OrgAnalyticsResponse,
} from '@jq4race/shared';
import { LocationAnalyticsResponseSchema, OrgAnalyticsResponseSchema } from '@jq4race/shared';

import { request } from './client.js';

function buildQueryString(params: AnalyticsRangeQuery): string {
  const entries: [string, string][] = [];
  if (params.from) entries.push(['from', params.from]);
  if (params.to) entries.push(['to', params.to]);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`;
}

/**
 * GET /admin/analytics/locations/:id
 *
 * Returns live KPI aggregates for a single location within an (optional)
 * date range. Server-enforced least-privilege: supervisors may only request
 * their own assigned locations (403 otherwise).
 */
export async function getLocationAnalytics(
  locationId: string,
  params: AnalyticsRangeQuery = {},
): Promise<LocationAnalyticsResponse> {
  const payload = await request<unknown>(
    `/admin/analytics/locations/${encodeURIComponent(locationId)}${buildQueryString(params)}`,
    { method: 'GET' },
  );
  return LocationAnalyticsResponseSchema.parse(payload);
}

/**
 * GET /admin/analytics/org
 *
 * Returns org-wide KPI totals plus a per-location breakdown table. org_admin
 * only — supervisors receive a 403 (no org-wide privilege, safe default).
 */
export async function getOrgAnalytics(
  params: AnalyticsRangeQuery = {},
): Promise<OrgAnalyticsResponse> {
  const payload = await request<unknown>(`/admin/analytics/org${buildQueryString(params)}`, {
    method: 'GET',
  });
  return OrgAnalyticsResponseSchema.parse(payload);
}
